import CookieConsent from 'react-cookie-consent';
import React, {Fragment, memo, useEffect, useState, useMemo} from 'react';
import Helmet from 'react-helmet';
import {IntlProvider, useIntl, FormattedMessage} from 'react-intl';
import {Provider as ReduxProvider} from 'react-redux';
import {ZoneManager} from 'use-zone-breakpoints';

import messagesDe from '../../tmp/data-extern/lang-de.json';
import messagesEnWithoutDefaults from '../../tmp/data-extern/lang-en.json';
import {store, useDispatch, useSelector} from '../store';

import {PagePropsContext} from '../hooks';

import {URL_PRIVACY_POLICY} from '../constants';

import style from './layout.module.scss';
import Footer from './footer';
import Header from './header';
import Nav from './nav';

const messagesEn = Object.assign(
	{},
	messagesDe,
	Object.fromEntries(
		Object.entries(messagesEnWithoutDefaults).filter(([_, msg]) => msg !== '')
	),
);

const Layout = memo(
	/**
	 * @param {{
	 *   children?: React.ReactNode,
	 * } & import("gatsby").PageProps<undefined, {
	 *     meta?: import("../page-metadata").PageMetadata,
	 *     locale?: "en" | "de",
	 * }>} props
	 * @returns {React.ReactElement} ...
	 */
	function Layout(props) {
		const {children, location, pageContext} = props;

		const pageOverlayed = useSelector((s) => s.pageOverlayed);
		const dispatch = useDispatch();

		const intl = useIntl();
		const [scrollBarGap, setScrollBarGap] = useState(0);

		// close nav on page switch
		useEffect(
			() => () => {
					dispatch({type: 'closeNav'});
				},
			[location.key, dispatch],
		);

		useEffect(
			() => {
				if (!pageOverlayed) {
					setTimeout(
						() => {
							const gap = window.innerWidth - document.documentElement.clientWidth;
							if (gap !== 0) {
								setScrollBarGap(gap);
							}
						},
						0,
					);
				}
			},
			[pageOverlayed],
		);

		return (
			<Fragment>
				<Helmet>
					<html lang={intl.locale || 'de'} />
					<meta charSet="utf-8" />
					<title>
						{`${intl.formatMessage({id: 'site.title'})}${
							pageContext.meta ?
							` - ${intl.formatMessage({id: pageContext.meta.titleI18nId})}` :
							''
						}`}
					</title>
					<style>
						{`
							:root {
								--scroll-bar-gap: ${scrollBarGap}px;
							}
						`}
					</style>

					<body
						className={`lp2020-page${pageOverlayed ? ' lp2020-page--overlayed' : ''}`}
					/>
				</Helmet>

				<Header />

				<div className={style.rootWrapper}>
					<Nav />

					<main className={style.content}>
						{children}
					</main>

					<Footer />
				</div>

				<CookieConsent
					location="none"
					disableStyles={true}
					containerClasses={style.cookieBanner}
					contentClasses={style.cookieBannerText}
					buttonClasses={style.cookieBannerOkButton}
					buttonText={(<FormattedMessage id="cookieBanner.okButton" />)}
				>
					<FormattedMessage
						id="cookieBanner.text"
						values={{
							more(...inner) {
								return (
									<a
										className={style.cookieBannerTextMore}
										href={URL_PRIVACY_POLICY}
									>
										{inner}
									</a>
								);
							},
						}}
					/>
				</CookieConsent>
			</Fragment>
		);
	}
);

export default memo(
	/**
	 * @param {{
	 *   children?: React.ReactNode,
	 * } & import("gatsby").PageProps<undefined, {
	 *     meta?: import("../page-metadata").PageMetadata,
	 *     locale?: "en" | "de",
	 * }>} props
	 * @returns {React.ReactElement} ...
	 */
	function LayoutWrapper(props) {
		const {pageContext: {locale}} = props;

		const pageProps = useMemo(
			() => {
				const newPageProps = {...props};
				delete newPageProps.children;
				delete newPageProps.data;
				return newPageProps;
			},
			[props],
		);

		return (
			<PagePropsContext.Provider value={pageProps}>
				<ReduxProvider store={store}>
					<IntlProvider
						locale={locale || 'de'}
						defaultLocale="de"
						messages={locale && locale === 'en' ? messagesEn : messagesDe}
					>
						<ZoneManager breakpoints={[768]} defaultZone={-1}>
							<Layout {...props} />
						</ZoneManager>
					</IntlProvider>
				</ReduxProvider>
			</PagePropsContext.Provider>
		);
	}
);
